import React, { useState } from 'react';
import styled from 'styled-components';
import { layout } from 'src/components/base';
import {
  PrimaryButton,
  OutlinedButton,
  TextButton,
} from '@prisjakt/ui/button/variants';

import { Row } from '@prisjakt/ui/list/variants';
import { TitleSmallText } from '@prisjakt/ui/text/variants';
import { useAppContext } from 'src/components/app-context';
import { useListContext } from '../../list-context';
import { RadioButton } from '@prisjakt/ui/radiobutton';
import type { List } from '../../types';
import Modal from 'src/components/modal-v2';
import { ModalBody, ModalFooter, ModalSizeEnum } from '@prisjakt/ui/modal';

type Props = {
  addToList: (listId?: number) => void;
  addToMyProducts: () => void;
  closeModal: () => void;
  isOpen: boolean;
  isDesktop: boolean;
  lists?: List[];
};

export const ListModal = (props: Props) => {
  const { addToList, addToMyProducts, closeModal, isOpen, isDesktop, lists } =
    props;

  const { t } = useAppContext();
  const { fetchMoreLists, moreLists, pageInfo } = useListContext();

  const [selectedValue, setSelectedValue] = useState<number | string>();
  const [listItemSelected, setListItemSelected] = useState<boolean>(false);

  const handleOptionClick = (listId: number | string) => {
    setSelectedValue(listId);

    if (!selectedValue) setListItemSelected(state => !state);
  };

  const onSubmit = () => {
    if (selectedValue === 'my-products') {
      addToMyProducts();
    } else if (typeof selectedValue === 'number') {
      addToList(selectedValue);
    }
    closeModal();
  };

  const handleSaveToNewListClick = () => {
    addToList();
    closeModal();
  };

  const allLists = [...(lists || []), ...(moreLists || [])];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      size={isDesktop ? ModalSizeEnum.NORMAL : ModalSizeEnum.FULL}
      title={`${t('lists', 'list.save-to-list')}:`}
    >
      <>
        <ModalBody>
          <ListWrapper>
            <li>
              <RadioButton
                id="my-products"
                name="list"
                selectedValue={selectedValue}
                value="my-products"
                text={t('lists', 'my-products.title')}
                onClickHandler={() => handleOptionClick('my-products')}
              />
            </li>

            {allLists.map(list => (
              <li key={list.id}>
                <RadioButton
                  id={list.id.toString()}
                  name="list"
                  selectedValue={selectedValue}
                  value={list.id}
                  text={list.name}
                  onClickHandler={() => handleOptionClick(list.id)}
                />
              </li>
            ))}

            {pageInfo?.hasNext && (
              <TextButton onClick={fetchMoreLists}>
                {t('common', 'button.show-more')}
              </TextButton>
            )}
          </ListWrapper>
        </ModalBody>
        <ModalFooter>
          <Row equallySizedChildren gap={1}>
            <OutlinedButton onClick={handleSaveToNewListClick}>
              <TitleSmallText>
                {t('lists', 'list.save-to-new-list')}
              </TitleSmallText>
            </OutlinedButton>
            <PrimaryButton onClick={onSubmit} disabled={!listItemSelected}>
              {t('common', 'button.save')}
            </PrimaryButton>
          </Row>
        </ModalFooter>
      </>
    </Modal>
  );
};

const ListWrapper = styled.ul`
  display: grid;
  gap: ${layout.measurements.spacing025};
  margin: 0;
  list-style: none;
  overflow: hidden;
`;
